// Friends & Family — private groups bottom sheet.
// MVP: list / create a group, share the invite link, see members, and toggle
// (opt-in, off by default) live-location and parked-car sharing.

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Users, Plus, Share2, Loader2, ChevronLeft, MapPin, Car, Radio, UserPlus } from "lucide-react";
import { toast } from "sonner";
import {
  createGroup,
  fetchMyGroups,
  fetchGroup,
  updateSharing,
  shareGroupInvite,
} from "../lib/groups";
import { getProfile } from "../lib/driverProfile";
import { shareReferral, INVITE_FF_MESSAGE } from "../lib/referral";
import { getCarLocation, formatRelativeTime } from "../lib/carLocation";
import { useBackdropDismiss } from "../lib/useBackdropDismiss";

export default function GroupsSheet({ open, onClose, userPos, onChanged }) {
  const dismissProps = useBackdropDismiss(open, onClose);
  const [groups, setGroups] = React.useState(null);
  const [maxGroups, setMaxGroups] = React.useState(3);
  const [activeId, setActiveId] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [name, setName] = React.useState("");
  const [creating, setCreating] = React.useState(false);

  const loadList = React.useCallback(async () => {
    try {
      const data = await fetchMyGroups();
      setGroups(data.groups || []);
      setMaxGroups(data.max_groups || 3);
    } catch {
      setGroups([]);
    }
  }, []);

  const loadDetail = React.useCallback(async (id) => {
    try {
      setDetail(await fetchGroup(id));
    } catch {
      toast.error("Could not open this group.");
      setActiveId(null);
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    setActiveId(null);
    setDetail(null);
    loadList();
  }, [open, loadList]);

  React.useEffect(() => {
    if (activeId) loadDetail(activeId);
  }, [activeId, loadDetail]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const clean = name.trim();
    if (clean.length < 2) {
      toast.error("Please enter a group name.");
      return;
    }
    setBusy(true);
    try {
      const g = await createGroup(clean, getProfile()?.nickname);
      setName("");
      setCreating(false);
      await loadList();
      setActiveId(g.id);
      toast.success("Group created.");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create the group.");
    } finally {
      setBusy(false);
    }
  };

  const setSharing = async (payload) => {
    setBusy(true);
    try {
      const g = await updateSharing(activeId, payload);
      setDetail(g);
      loadList();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update sharing.");
    } finally {
      setBusy(false);
    }
  };

  const toggleLive = () => {
    if (detail?.my_share_live) {
      setSharing({ share_live: false });
      toast.success("Location sharing stopped.");
      return;
    }
    if (!userPos) {
      toast.error("We need your current location first — tap the crosshair on the map.");
      return;
    }
    setSharing({ share_live: true, lat: userPos.lat, lng: userPos.lng, nickname: getProfile()?.nickname });
    toast.success("Sharing your location with this group.");
  };

  const toggleCar = () => {
    if (detail?.my_share_car) {
      setSharing({ share_car: false });
      toast.success("Parked car is private again.");
      return;
    }
    const car = getCarLocation();
    if (!car) {
      toast.error("Save your car first (My car → Save my car), then share it.");
      return;
    }
    setSharing({
      share_car: true,
      car: { lat: car.lat, lng: car.lng, saved_at: car.saved_at, note: car.note || null },
      nickname: getProfile()?.nickname,
    });
    toast.success("Parked car shared with this group.");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[3500] bg-black/75"
            {...dismissProps}
            data-testid="groups-backdrop"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 280 }}
            className="fixed inset-x-0 bottom-0 z-[3501] bg-[#141416] border-t border-white/10 rounded-t-3xl p-5 pb-8 max-h-[92vh] overflow-y-auto sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:bottom-6 sm:rounded-3xl sm:border sm:w-[440px] sm:max-w-[92vw]"
            data-testid="groups-sheet"
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
                  Friends &amp; Family
                </div>
                <div className="font-heading text-xl font-semibold text-white mt-1 leading-tight truncate" data-testid="groups-title">
                  {detail ? detail.name : "Your private groups"}
                </div>
                <p className="text-[12px] text-zinc-400 mt-1 leading-snug">
                  Share parking and location privately with people you trust.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 transition-transform"
                data-testid="groups-close"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            {!activeId ? (
              /* ============ LIST ============ */
              <div data-testid="groups-list-view">
                {groups === null && (
                  <div className="py-6 flex justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-[#ccff00]" />
                  </div>
                )}

                {groups?.length === 0 && !creating && (
                  <p className="text-[13px] text-zinc-400 leading-snug" data-testid="groups-empty">
                    No groups yet. Create one and send the invite link to your family or friends.
                  </p>
                )}

                {groups?.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setActiveId(g.id)}
                    className="w-full mt-2 p-3 rounded-2xl bg-[#0a0a0c] border border-white/10 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
                    data-testid={`group-item-${g.id}`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-[#ccff00]/10 border border-[#ccff00]/30 flex items-center justify-center shrink-0">
                      <Users className="w-4 h-4 text-[#ccff00]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-white truncate">{g.name}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        {g.member_count} member{g.member_count === 1 ? "" : "s"}
                        {g.my_share_live ? " · sharing location" : ""}
                        {g.my_share_car ? " · car shared" : ""}
                      </div>
                    </div>
                    <ChevronLeft className="w-4 h-4 text-zinc-600 rotate-180" />
                  </button>
                ))}

                {creating ? (
                  <form onSubmit={handleCreate} className="mt-4" data-testid="group-create-form">
                    <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                      Group name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value.slice(0, 40))}
                      placeholder="e.g. Family"
                      className="mt-1.5 w-full h-11 px-3 rounded-xl bg-[#0a0a0c] border border-white/10 text-white text-sm outline-none focus:border-[#ccff00]/60 transition-colors"
                      data-testid="group-name-input"
                    />
                    <button
                      type="submit"
                      disabled={busy}
                      className="mt-3 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
                      data-testid="group-create-submit"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Create group
                    </button>
                  </form>
                ) : (
                  groups !== null &&
                  groups.length < maxGroups && (
                    <button
                      type="button"
                      onClick={() => setCreating(true)}
                      className="mt-4 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                      data-testid="group-new-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Create a group
                    </button>
                  )
                )}

                <button
                  type="button"
                  onClick={() =>
                    shareReferral(
                      (kind, msg) => {
                        if (kind === "success") toast.success(msg);
                        else if (kind === "error") toast.error(msg);
                      },
                      { title: "SpotDrop Malta", text: INVITE_FF_MESSAGE, copyMessage: "Link copied!" }
                    )
                  }
                  className="mt-2 w-full h-12 rounded-full bg-[#0a0a0c] border border-white/15 text-white text-[13px] font-medium inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                  data-testid="groups-invite-ff-btn"
                >
                  <UserPlus className="w-4 h-4 text-[#ccff00]" />
                  Invite Friends &amp; Family
                </button>

                <p className="mt-3 text-[10px] text-zinc-500 text-center leading-relaxed">
                  Groups are private. Location sharing is off until you turn it on.{" "}
                  <a href="/privacy" className="text-zinc-400 underline" data-testid="groups-privacy-link">
                    Privacy &amp; data
                  </a>
                </p>
              </div>
            ) : (
              /* ============ DETAIL ============ */
              <div data-testid="group-detail-view">
                <button
                  type="button"
                  onClick={() => { setActiveId(null); setDetail(null); }}
                  className="inline-flex items-center gap-1.5 text-[12px] text-zinc-400 mb-3"
                  data-testid="group-back-btn"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> All groups
                </button>

                {!detail ? (
                  <div className="py-6 flex justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-[#ccff00]" />
                  </div>
                ) : (
                  <>
                    <div className="p-3 rounded-2xl bg-[#0a0a0c] border border-white/10">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                        Members ({detail.member_count})
                      </div>
                      <div className="mt-2 space-y-2" data-testid="group-members">
                        {detail.members.map((m, i) => (
                          <div key={`${m.nickname}-${i}`} className="flex items-center gap-2.5" data-testid={`group-member-${i}`}>
                            <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-[11px] font-semibold text-zinc-300">
                              {(m.nickname || "D").slice(0, 1).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-[13px] text-white truncate">
                                {m.nickname}
                                {m.me && <span className="text-zinc-500 text-[11px]"> · you</span>}
                              </div>
                              <div className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5">
                                {m.sharing_live ? (
                                  <span className="inline-flex items-center gap-1 text-[#ccff00]">
                                    <Radio className="w-3 h-3" /> {m.live_fresh ? "Live" : "Paused"}
                                  </span>
                                ) : (
                                  <span>Not sharing</span>
                                )}
                                {m.sharing_car && (
                                  <span className="inline-flex items-center gap-1 text-zinc-300">
                                    <Car className="w-3 h-3" /> Car shared
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        shareGroupInvite(detail.code, detail.name, (kind, msg) => {
                          if (kind === "success") toast.success(msg);
                          else if (kind === "error") toast.error(msg);
                        })
                      }
                      className="mt-3 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                      data-testid="group-invite-btn"
                    >
                      <Share2 className="w-4 h-4" />
                      Invite to this group
                    </button>

                    <button
                      type="button"
                      onClick={toggleLive}
                      disabled={busy}
                      className={`mt-2 w-full h-12 rounded-full text-[13px] font-medium inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60 ${
                        detail.my_share_live
                          ? "bg-red-500/10 border border-red-500/40 text-red-200"
                          : "bg-[#0a0a0c] border border-white/15 text-white"
                      }`}
                      data-testid="group-share-location-btn"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                      {detail.my_share_live ? "Stop sharing my location" : "Share my location"}
                    </button>

                    <button
                      type="button"
                      onClick={toggleCar}
                      disabled={busy}
                      className={`mt-2 w-full h-12 rounded-full text-[13px] font-medium inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60 ${
                        detail.my_share_car
                          ? "bg-red-500/10 border border-red-500/40 text-red-200"
                          : "bg-[#0a0a0c] border border-white/15 text-white"
                      }`}
                      data-testid="group-share-car-btn"
                    >
                      <Car className="w-4 h-4" />
                      {detail.my_share_car ? "Stop sharing my parked car" : "Share my parked car"}
                    </button>

                    {detail.my_share_live && (
                      <div className="mt-3 p-2.5 rounded-lg bg-[#ccff00]/10 border border-[#ccff00]/30 text-[12px] text-[#ccff00] flex items-start gap-1.5" data-testid="group-sharing-active">
                        <Radio className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>
                          Sharing is active while SpotDrop is open. It stops automatically after an hour.
                        </span>
                      </div>
                    )}

                    {(() => {
                      const car = getCarLocation();
                      if (!car) return null;
                      return (
                        <p className="mt-3 text-[10px] text-zinc-500 text-center leading-relaxed">
                          Your car was saved {formatRelativeTime(car.saved_at)}. Only shared while the toggle above is on.
                        </p>
                      );
                    })()}
                  </>
                )}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
